import { observer } from "mobx-react-lite"

import type { ManagePositionStoreBase } from "../../stores/manage-position-store-base"

import { ManageSettingsPopoverView } from "./manage-settings-popover-view"

/**
 * Connects the shared `ManagePositionStoreBase.slippage/deadline` surface to
 * the presentational `ManageSettingsPopoverView`.
 *
 * All four manage stores (increase / decrease / collect / burn) own their own
 * base instance. The composite takes a `primary` store as the display source
 * of truth and fans writes out to every store in `stores` so the settings
 * stay in sync across tabs.
 *
 * The deadline is kept on the base as an absolute Unix timestamp (seconds);
 * the view edits minutes-from-now and we convert on every write, which is
 * cheap and also refreshes the base's "now" each time the user touches the
 * field — preventing stale deadlines when a tab has been open for a while.
 */
export const ManageSettingsPopoverComposite = observer(
  ({
    primary,
    stores,
    className,
  }: {
    primary: ManagePositionStoreBase
    stores: ReadonlyArray<ManagePositionStoreBase>
    className?: string
  }) => {
    const handleSlippageChange = (next: number): void => {
      for (const store of stores) store.setSlippage(next)
    }

    const handleDeadlineMinutesChange = (minutes: number): void => {
      const seconds = Math.floor(Date.now() / 1000) + Math.max(0, minutes) * 60
      for (const store of stores) store.setDeadline(seconds)
    }

    return (
      <ManageSettingsPopoverView
        slippage={primary.slippage}
        onSlippageChange={handleSlippageChange}
        deadlineMinutes={primary.deadlineMinutes}
        onDeadlineMinutesChange={handleDeadlineMinutesChange}
        className={className}
      />
    )
  },
)

ManageSettingsPopoverComposite.displayName = "ManageSettingsPopoverComposite"
